import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ChevronDown, Check, Search, X, Plus, Loader2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

let cachedCustomers = null;

export function useCustomers() {
  const [customers, setCustomers] = useState(cachedCustomers || []);
  const [loading, setLoading] = useState(!cachedCustomers);

  const refresh = () => {
    cachedCustomers = null;
    api.get(`${API}/api/customers`)
      .then(res => { cachedCustomers = res.data; setCustomers(res.data); })
      .catch(() => {});
  };

  useEffect(() => {
    if (cachedCustomers) return;
    api.get(`${API}/api/customers`)
      .then(res => { cachedCustomers = res.data; setCustomers(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { customers, loading, refresh };
}

export function ClientSelect({ value, onChange, placeholder = 'Select client...', className = '' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const { customers, refresh } = useCustomers();

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.customer_code.toLowerCase().includes(q) || c.customer_name.toLowerCase().includes(q);
  });

  const selected = customers.find(c => c.customer_code === value || c.customer_name === value);
  const displayValue = selected ? `${selected.customer_code} — ${selected.customer_name}` : value || '';

  const handleCreate = async () => {
    if (!newCode.trim() || !newName.trim()) { toast.error('Code and Name are required'); return; }
    setSaving(true);
    try {
      const res = await api.post(`${API}/api/customers`, { customer_code: newCode.trim(), customer_name: newName.trim() });
      toast.success(`Client ${res.data.customer_code} created`);
      refresh();
      onChange(res.data.customer_code);
      setCreating(false); setNewCode(''); setNewName(''); setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create client'); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreating(false); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className={`justify-between font-normal text-left h-9 ${!displayValue ? 'text-muted-foreground' : ''} ${className}`}
          data-testid="client-select-trigger"
        >
          <span className="truncate text-xs">{displayValue || placeholder}</span>
          <div className="flex items-center gap-0.5 ml-1 flex-shrink-0">
            {value && (
              <span className="hover:text-destructive cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(''); }}>
                <X size={12} />
              </span>
            )}
            <ChevronDown size={14} className="opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        {creating ? (
          <div className="p-3 space-y-2.5">
            <p className="text-xs font-semibold text-foreground">Create New Client</p>
            <Input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} placeholder="Client Code (e.g. DXY01)" className="h-8 text-xs font-mono" data-testid="new-client-code" />
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Client Name" className="h-8 text-xs" data-testid="new-client-name" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setCreating(false)}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs flex-1" onClick={handleCreate} disabled={saving} data-testid="save-new-client">
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center border-b px-2">
              <Search size={14} className="text-muted-foreground mr-1.5" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by code or name..."
                className="border-0 h-8 text-xs focus-visible:ring-0 shadow-none"
                data-testid="client-select-search"
              />
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No clients found</p>
              )}
              {filtered.map(c => (
                <button
                  key={c.customer_code}
                  onClick={() => { onChange(c.customer_code); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 hover:bg-accent transition-colors ${
                    (value === c.customer_code || value === c.customer_name) ? 'bg-accent/50' : ''
                  }`}
                  data-testid={`client-option-${c.customer_code}`}
                >
                  <span className="font-mono font-semibold text-[11px] min-w-[70px] text-primary">{c.customer_code}</span>
                  <span className="text-muted-foreground truncate">{c.customer_name}</span>
                  {(value === c.customer_code || value === c.customer_name) && (
                    <Check size={12} className="ml-auto text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t p-1.5">
              <Button variant="ghost" size="sm" className="w-full h-7 text-xs text-primary justify-start" onClick={() => setCreating(true)} data-testid="create-new-client-btn">
                <Plus size={12} className="mr-1" />Create New Client
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
